import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../lib/axios'; // baseURL ya incluye /api
import { toast } from 'sonner';
import { formatInt } from '../lib/dashboardFormat';
import { RefreshCw, Loader2 } from 'lucide-react';

function formatBytes(n) {
  if (!n) return '0 B';
  if (n < 1024) return n + ' B';
  if (n < 1048576) return (n / 1024).toFixed(1) + ' KB';
  return (n / 1048576).toFixed(1) + ' MB';
}

function Tile({ label, value, sub }) {
  return (
    <div className='rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-white/60 dark:bg-slate-800/40'>
      <div className='text-xs text-slate-500 dark:text-slate-400'>{label}</div>
      <div className='text-xl font-bold mt-1'>{value}</div>
      {sub && <div className='text-xs text-slate-500 mt-0.5'>{sub}</div>}
    </div>
  );
}

export default function ContentStatsCard() {
  const [data, setData] = useState({ overview: null, users: [], activity: null, retention: null });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [o, u, a, r] = await Promise.all([
        axiosInstance.get('/admin/stats/overview'),
        axiosInstance.get('/admin/stats/users'),
        axiosInstance.get('/admin/stats/activity'),
        axiosInstance.get('/admin/stats/retention'),
      ]);
      setData({ overview: o.data, users: u.data || [], activity: a.data, retention: r.data });
    } catch (e) {
      toast.error('No se pudieron cargar las estadisticas de contenido');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const ov = data.overview || {};
  const rt = data.retention || {};

  return (
    <div className='glass rounded-xl p-6 space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-lg font-semibold'>Aislamiento y contenido</h3>
        <button onClick={fetchAll} className='text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && !data.overview ? (
        <Loader2 className='w-5 h-5 animate-spin text-slate-400' />
      ) : (
        <>
          {/* Overview — solo metricas, nunca contenido */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
            <Tile label='Usuarios' value={formatInt(ov.users_total)} />
            <Tile label='Activos 30d' value={formatInt(ov.users_active_30d)} />
            <Tile label='Docs activos' value={formatInt(ov.docs_active)} sub={'de ' + formatInt(ov.docs_total) + ' totales'} />
            <Tile label='Docs en papelera' value={formatInt(ov.docs_deleted)} />
            <Tile label='Hojas' value={formatInt(ov.sheets_total)} />
            <Tile label='Imagenes' value={formatInt(ov.images_total)} />
            <Tile label='Almacenamiento' value={formatBytes(ov.storage_bytes)} />
          </div>

          {/* Retencion */}
          <div className='space-y-2'>
            <h4 className='text-sm font-semibold'>Retencion</h4>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
              <Tile label='Por archivar' value={formatInt(rt.docs_due_to_archive)} />
              <Tile label='Por purgar' value={formatInt(rt.docs_due_to_purge)} />
              <Tile label='En hold' value={formatInt(rt.docs_held_skipped)} />
              <Tile label='Holds activos' value={formatInt(rt.active_holds)} />
            </div>
            <p className='text-xs text-slate-500'>{'Cold storage: ' + (rt.cold_storage_enabled ? 'ON' : 'OFF')}</p>
          </div>

          {/* Actividad por accion */}
          <div className='space-y-2'>
            <h4 className='text-sm font-semibold'>Actividad (por accion)</h4>
            <div className='space-y-1'>
              {(data.activity?.by_action || []).map(a => (
                <div key={a.action} className='flex justify-between text-sm'>
                  <span>{a.action}</span>
                  <span>{formatInt(a.count)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tabla por usuario */}
          <div className='space-y-2'>
            <h4 className='text-sm font-semibold'>Por usuario</h4>
            <div className='overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead>
                  <tr>
                    {['Usuario', 'Docs', 'Activos', 'Papelera', 'Hojas', 'Imagenes', 'Bytes', 'Ult. actividad'].map(h => (
                      <th key={h} className='text-left text-xs text-slate-500 pr-2 pb-1'>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.users || []).map(u => (
                    <tr key={u.user_id} className='border-t border-slate-100 dark:border-slate-700'>
                      <td className='pr-2 py-1'>{u.user_id?.slice(0, 8)}</td>
                      <td className='pr-2 py-1'>{formatInt(u.docs_total)}</td>
                      <td className='pr-2 py-1'>{formatInt(u.docs_active)}</td>
                      <td className='pr-2 py-1'>{formatInt(u.docs_deleted)}</td>
                      <td className='pr-2 py-1'>{formatInt(u.sheets_total)}</td>
                      <td className='pr-2 py-1'>{formatInt(u.images_total)}</td>
                      <td className='pr-2 py-1'>{formatBytes(u.storage_bytes)}</td>
                      <td className='pr-2 py-1'>{u.last_activity_at ? String(u.last_activity_at).slice(0, 10) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
